import { useCallback, useMemo, useState } from 'react'
import Badge from '@mui/material/Badge'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import { useColorScheme, useTheme } from '@mui/material/styles'
import Tooltip from '@mui/material/Tooltip'
import { Bell as BellIcon } from '@phosphor-icons/react/dist/ssr/Bell'
import { List as ListIcon } from '@phosphor-icons/react/dist/ssr/List'
import { Moon as MoonIcon } from '@phosphor-icons/react/dist/ssr/Moon'
import { Sun as SunIcon } from '@phosphor-icons/react/dist/ssr/Sun'
import { useApiUrl, useCustomMutation, useOne } from '@refinedev/core'

import type { NavItemConfig } from '@/types/nav'
import { PLATFORM_LIVE_CHANNELS } from '@/lib/platform-live-channels'
import { setSettings as setPersistedSettings } from '@/lib/settings/set-settings'
import { useHealthAccess } from '@/hooks/use-health-access'
import { useLiveRefetch } from '@/hooks/use-live-refetch'
import { usePopover } from '@/hooks/use-popover'
import { useSettings } from '@/hooks/use-settings'
import { useUser } from '@/hooks/use-user'
import { ProductUpdatesButton } from '@/components/core/product-updates'
import { UserAvatar } from '@/components/core/user-avatar'
import { AskAiButton } from '@/components/dashboard/assistant/ask-ai-button'
import { Omnibar } from '@/components/dashboard/assistant/omnibar'
// import { SystemStatusButton } from '@/components/core/system-status'
import type { ColorScheme } from '@/styles/theme/types'

import { NotificationsPopover } from '../notifications-popover'
import type { UserNotification } from '../notifications-popover'
import { UserPopover } from '../user-popover/user-popover'
import { MobileNav } from './mobile-nav'

// Cabinet health notifications bell is hidden for now; flip to re-enable.
const SHOW_HEALTH_NOTIFICATIONS = false

export interface MainNavProps {
	items: NavItemConfig[]
	sidebarMini?: boolean
}

export function MainNav({ items, sidebarMini }: MainNavProps) {
	const [openNav, setOpenNav] = useState<boolean>(false)
	const hasHealthAccess = useHealthAccess()

	return (
		<>
			<Box
				component="header"
				data-test="main-nav"
				sx={{
					'--MainNav-background': 'var(--mui-palette-background-default)',
					'--MainNav-divider': 'var(--mui-palette-divider)',
					bgcolor: 'var(--MainNav-background)',
					left: 0,
					position: 'sticky',
					pt: { lg: 'var(--Layout-gap)' },
					top: 'calc(var(--ImpersonationBanner-height, 0px) + var(--BetterStackBanner-height, 0px))',
					zIndex: 'var(--MainNav-zIndex)',
				}}
			>
				<Box
					sx={{
						borderBottom: '1px solid var(--MainNav-divider)',
						display: 'flex',
						flex: '1 1 auto',
						gap: { xs: 1, lg: 0 },
						minHeight: 'var(--MainNav-height)',
						px: { xs: 2, lg: 3 },
						py: 1,
						position: 'relative',
					}}
				>
					<Stack direction="row" spacing={1} sx={{ alignItems: 'center', flex: '0 0 auto' }}>
						<IconButton
							aria-label="Open navigation menu"
							data-test="mobile-nav-toggle"
							onClick={(): void => {
								setOpenNav(true)
							}}
							edge="start"
							sx={{ display: { xs: 'inline-flex', lg: 'none' } }}
						>
							<ListIcon />
						</IconButton>
						<Omnibar />
					</Stack>
					<Stack
						direction="row"
						spacing={{ xs: 1, lg: 2 }}
						sx={{
							alignItems: 'center',
							flex: '1 1 auto',
							justifyContent: 'flex-end',
						}}
					>
						<AskAiButton />
						{/* <SystemStatusButton /> */}
						<ProductUpdatesButton />
						{SHOW_HEALTH_NOTIFICATIONS && hasHealthAccess ? <NotificationsButton /> : null}
						<Box sx={{ display: { xs: 'none', lg: 'inline-flex' } }}>
							<ColorSchemeButton />
						</Box>
						<UserButton />
					</Stack>
				</Box>
			</Box>
			<MobileNav
				items={items}
				onClose={() => {
					setOpenNav(false)
				}}
				open={openNav}
			/>
		</>
	)
}

function NotificationsButton() {
	const popover = usePopover<HTMLButtonElement>()
	const apiUrl = useApiUrl()
	const { mutate } = useCustomMutation()

	const { data, refetch } = useOne<{ notifications: UserNotification[]; unread_count: number }>({
		resource: 'users/me',
		id: 'notifications',
	})

	useLiveRefetch({
		channel: PLATFORM_LIVE_CHANNELS.USER_NOTIFICATIONS,
		refetch,
	})

	const notifications = useMemo(() => data?.data?.notifications ?? [], [data?.data?.notifications])
	const unreadCount = data?.data?.unread_count ?? 0

	const handleMarkAllAsRead = useCallback(() => {
		mutate(
			{
				url: `${apiUrl}/users/me/notifications/mark-read`,
				method: 'put',
				values: {},
			},
			{
				onSuccess: () => {
					refetch()
				},
			}
		)
	}, [apiUrl, mutate, refetch])

	return (
		<>
			<Tooltip title="Notifications">
				<Badge
					color="error"
					invisible={unreadCount === 0}
					sx={{
						'& .MuiBadge-dot': {
							borderRadius: '50%',
							height: '10px',
							right: '6px',
							top: '6px',
							width: '10px',
						},
					}}
					variant="dot"
				>
					<IconButton onClick={popover.handleOpen} ref={popover.anchorRef}>
						<BellIcon />
					</IconButton>
				</Badge>
			</Tooltip>
			<NotificationsPopover
				anchorEl={popover.anchorRef.current}
				notifications={notifications}
				onClose={popover.handleClose}
				onMarkAllAsRead={handleMarkAllAsRead}
				open={popover.open}
			/>
		</>
	)
}

function ColorSchemeButton() {
	const theme = useTheme()
	const { setColorScheme } = useColorScheme()
	const { settings, setSettings } = useSettings()

	const updateColorScheme = (mode: ColorScheme): void => {
		setColorScheme(mode)

		const updatedSettings = { ...settings, colorScheme: mode }
		setPersistedSettings(updatedSettings)
		setSettings(updatedSettings)
	}

	return (
		<Tooltip title="Theme">
			{theme.palette.mode === 'dark' ? (
				<IconButton
					aria-label="Switch to light theme"
					data-test="theme-toggle"
					onClick={() => {
						updateColorScheme('light')
					}}
				>
					<SunIcon />
				</IconButton>
			) : (
				<IconButton
					aria-label="Switch to dark theme"
					data-test="theme-toggle"
					onClick={() => {
						updateColorScheme('dark')
					}}
				>
					<MoonIcon />
				</IconButton>
			)}
		</Tooltip>
	)
}

function UserButton() {
	const popover = usePopover<HTMLButtonElement>()
	const { user } = useUser()

	return (
		<>
			<Box
				aria-label="Open profile menu"
				data-test="user-menu-button"
				component="button"
				onClick={popover.handleOpen}
				ref={popover.anchorRef}
				sx={{ border: 'none', background: 'transparent', cursor: 'pointer', p: 0 }}
			>
				<Badge
					anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
					color="success"
					sx={{
						'& .MuiBadge-dot': {
							border: '2px solid var(--MainNav-background)',
							borderRadius: '50%',
							bottom: '6px',
							height: '12px',
							right: '6px',
							width: '12px',
						},
					}}
					variant="dot"
				>
					<UserAvatar name={user?.name} src={user?.avatar} />
				</Badge>
			</Box>
			<UserPopover anchorEl={popover.anchorRef.current} onClose={popover.handleClose} open={popover.open} />
		</>
	)
}
